import React, { useEffect, useMemo, useState } from "react";
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, BanknotesIcon, ChartBarSquareIcon } from "@heroicons/react/24/outline";
import { Line } from "react-chartjs-2";
import { CategoryScale, Chart as ChartJS, Filler, Legend, LineElement, LinearScale, PointElement, Tooltip } from "chart.js";
import EmptyState from "../components/EmptyState";
import FormField from "../components/FormField";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import SegmentedControl from "../components/SegmentedControl";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { subscribeByOwner } from "../services/db";
import { formatMoneyWithUnit } from "../utils/format";
import { buildMonthlyNetChart, buildPeriodRange, calculateProjectsOverview, filterTransactionsByRange, sumTransactions } from "../utils/report";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default function ReportsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [periodType, setPeriodType] = useState("month");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribers = [subscribeByOwner("transactions", user.uid, setTransactions), subscribeByOwner("projects", user.uid, setProjects)];
    const timer = setTimeout(() => setReady(true), 250);
    return () => {
      unsubscribers.forEach((unsub) => unsub?.());
      clearTimeout(timer);
    };
  }, [user?.uid]);

  const range = useMemo(() => buildPeriodRange(periodType, selectedDate), [periodType, selectedDate]);
  const periodTransactions = useMemo(() => filterTransactionsByRange(transactions, range.start, range.end), [range.end, range.start, transactions]);
  const summary = useMemo(() => sumTransactions(periodTransactions), [periodTransactions]);
  const projectsOverview = useMemo(() => calculateProjectsOverview(projects), [projects]);
  const monthChart = useMemo(() => buildMonthlyNetChart(transactions), [transactions]);

  const chartData = useMemo(() => ({
    labels: monthChart.labels,
    datasets: [{ label: "صافي الأشهر الأخيرة", data: monthChart.values, borderColor: "#6D202E", backgroundColor: "rgba(109, 32, 46, 0.12)", fill: true, tension: 0.35 }]
  }), [monthChart]);

  if (!ready) return <LoadingScreen text="جارٍ تحميل التقارير..." />;

  return (
    <div className="page-shell space-y-4">
      <SectionCard title="فلتر التقارير" subtitle="اختر نوع التقرير والتاريخ المرجعي">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-2">
            <span className="field-label">نوع الفترة</span>
            <SegmentedControl value={periodType} onChange={setPeriodType} options={[{ key: "day", value: "day", label: "يومي" }, { key: "week", value: "week", label: "أسبوعي" }, { key: "month", value: "month", label: "شهري" }]} columnsClass="grid-cols-3" />
          </div>
          <FormField type="date" dir="ltr" label="التاريخ المرجعي" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
        </div>
      </SectionCard>

      <section className="mini-grid">
        <StatCard title="إجمالي الدخل" value={formatMoneyWithUnit(summary.income)} icon={ArrowTrendingUpIcon} tone="success" />
        <StatCard title="إجمالي المصاريف" value={formatMoneyWithUnit(summary.expense)} icon={ArrowTrendingDownIcon} tone="danger" />
        <StatCard title="الصافي" value={formatMoneyWithUnit(summary.net)} icon={BanknotesIcon} tone="brand" />
        <StatCard title="المتبقي على المشاريع" value={formatMoneyWithUnit(projectsOverview.remaining)} icon={ChartBarSquareIcon} tone="accent" />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <SectionCard title="اتجاه الصافي المالي" subtitle="ملخص آخر 6 أشهر">
          <div className="chart-box">
            <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" } } }} />
          </div>
        </SectionCard>

        <SectionCard title="ملخص الفترة المختارة" subtitle={`${range.start} إلى ${range.end}`}>
          <div className="space-y-3">
            <div className="rounded-2xl bg-emerald-50 p-4"><div className="text-sm text-slate-500">الدخل</div><div className="mt-1 text-xl font-extrabold text-income sm:text-2xl">{formatMoneyWithUnit(summary.income)}</div></div>
            <div className="rounded-2xl bg-rose-50 p-4"><div className="text-sm text-slate-500">المصاريف</div><div className="mt-1 text-xl font-extrabold text-expense sm:text-2xl">{formatMoneyWithUnit(summary.expense)}</div></div>
            <div className="rounded-2xl bg-brand-50 p-4"><div className="text-sm text-slate-500">الصافي</div><div className="mt-1 text-xl font-extrabold text-brand-800 sm:text-2xl">{formatMoneyWithUnit(summary.net)}</div></div>
          </div>
        </SectionCard>
      </section>

      <SectionCard title="تفاصيل الحركات ضمن الفترة" subtitle="كل العمليات المالية الداخلة ضمن التقرير الحالي">
        {!periodTransactions.length ? (
          <EmptyState title="لا توجد بيانات في هذه الفترة" description="جرّب تغيير نوع الفترة أو التاريخ المرجعي." />
        ) : (
          <div className="table-shell"><div className="table-wrap"><table className="min-w-full bg-white text-right"><thead className="bg-slate-50 text-xs text-slate-600 sm:text-sm"><tr><th className="px-3 py-3 font-extrabold">العنوان</th><th className="px-3 py-3 font-extrabold">النوع</th><th className="px-3 py-3 font-extrabold">التصنيف</th><th className="px-3 py-3 font-extrabold">التاريخ</th><th className="px-3 py-3 font-extrabold">المبلغ</th></tr></thead><tbody>{periodTransactions.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map((item) => (<tr key={item.id} className="border-t border-slate-100"><td className="px-3 py-3 font-bold text-slate-800">{item.title}</td><td className="px-3 py-3"><span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${item.type === "income" ? "bg-emerald-50 text-income" : "bg-rose-50 text-expense"}`}>{item.type === "income" ? "دخل" : "مصروف"}</span></td><td className="px-3 py-3 text-slate-600">{item.category || "—"}</td><td className="px-3 py-3 text-slate-600">{item.date || "—"}</td><td className={`px-3 py-3 font-extrabold ${item.type === "income" ? "text-income" : "text-expense"}`}>{item.type === "income" ? "+" : "-"}{formatMoneyWithUnit(item.amount)}</td></tr>))}</tbody></table></div></div>
        )}
      </SectionCard>
    </div>
  );
}
